import { useQuery, useQueryClient } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'

export const USE_GET_GUEST_PASS_PROFILE_QUERY_KEY = 'useGetGuestPassProfile'

interface GetGuestPassProfileRequest {
  clientId: string
  skip: number
  limit: number
}

export const getGuestPassProfileQueryKey = (data: GetGuestPassProfileRequest) => [
  USE_GET_GUEST_PASS_PROFILE_QUERY_KEY,
  data,
]

export type GuestPassVisit = {
  id: number
  createdAt: number
  guestCount: number
  placeId: { id: number; name: string; buildingId: number } | null
  // Note: Fields below are returned by the BE and needed by the legacy VisitDetailsModal
  clientId: {
    id: number
    buildingId: number
    user: { firstName: string; lastName: string }
    phone: string | null
    street: string | null
    birth: string | null
  } | null
  // Note: shuffled id of THIS visit's own client — not necessarily the profile owner (a
  // guest-pass account can have visits logged by different household members). Pass this,
  // not the profile-level `id`, to GET /guest-pass/{code}/details when editing a visit.
  userCode?: string
  householdMembers: Array<{
    id: number
    clientId: { id: number; user: { firstName: string; lastName: string }; birth: string | null }
  }>
}

export type GuestPassProfileResponse = {
  id: string
  totalGuestPasses: number
  usedGuestPasses: number
  guestPassVisits: GuestPassVisit[]
  total: number
}

const getGuestPassProfile = async (data: GetGuestPassProfileRequest) => {
  const { clientId, skip, limit } = data
  const response = await authAPI.get<GuestPassProfileResponse>(`/guest-pass/${clientId}/profile`, {
    params: { limit, skip },
  })
  return response.data
}

export const useGetGuestPassProfile = (data: GetGuestPassProfileRequest) => {
  return useQuery({
    enabled: !!data.clientId,
    queryFn: () => getGuestPassProfile(data),
    queryKey: getGuestPassProfileQueryKey(data),
  })
}

export const useInvalidateGuestPassProfile = () => {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({
      queryKey: [USE_GET_GUEST_PASS_PROFILE_QUERY_KEY],
    })
}
